import dayjs, { Dayjs } from "dayjs";
// validTo is DateOnly on the wire, so DateReviver hands over a Date at UTC midnight;
// dayjs() on that names the previous day west of Greenwich and every rule fires (and
// interpolates {{daysUntilExpiry}}) a day early. Same helper validity.ts uses.
import { fromDateOnly } from "../../../utils/DateOnly";

// Client-configurable profile banner rules. Stored per client as JSON in
// client_setting (banners.rules) and evaluated entirely on the frontend in
// StatusBanner — the data needed (member.validTo) is already loaded, so there
// is no API round-trip. When banners.rules is empty/absent the profile behaves
// exactly as before (only the existing hardcoded banners show).

export type BannerSeverity = 'info' | 'success' | 'warning' | 'error';

// `type` is an open string so new rule types (paymentFailed, …) can be added
// without a frontend change; today only membershipExpiry is evaluated.
export interface BannerRule {
    id: string;
    type: string;
    thresholdDays: number;
    severity: BannerSeverity;
    message: string;
}

export interface EvaluatedBanner {
    severity: BannerSeverity;
    message: string;
}

const SEVERITY_RANK: Record<BannerSeverity, number> = {
    info: 0,
    success: 1,
    warning: 2,
    error: 3,
};

// Validate a single rule's shape. banners.rules is operator-editable JSON, so a
// syntactically-valid blob may still contain nulls, primitives, or objects with
// missing/mistyped fields — those must be dropped, not handed to evaluation.
function isValidRule(rule: unknown): rule is BannerRule {
    if (rule === null || typeof rule !== 'object') return false;
    const r = rule as Record<string, unknown>;
    return typeof r.id === 'string'
        && typeof r.type === 'string'
        && typeof r.thresholdDays === 'number'
        && Number.isFinite(r.thresholdDays)
        && typeof r.message === 'string'
        && (r.severity === 'info' || r.severity === 'success' || r.severity === 'warning' || r.severity === 'error');
}

// Tolerant parse of the raw banners.rules JSON. A malformed blob, or any rule
// with the wrong shape, is dropped so evaluation never throws and never renders
// an invalid Alert severity — at worst the affected rule simply doesn't fire.
export function parseBannerRules(json: string | undefined | null): BannerRule[] {
    if (!json) return [];
    try {
        const parsed = JSON.parse(json);
        const rules = Array.isArray(parsed) ? parsed : parsed?.rules;
        return Array.isArray(rules) ? rules.filter(isValidRule) : [];
    } catch {
        return [];
    }
}

function interpolate(message: string, tokens: Record<string, string | number>): string {
    return message.replace(/\{\{(\w+)\}\}/g, (match, key) =>
        key in tokens ? String(tokens[key]) : match,
    );
}

// Evaluate membershipExpiry rules against the member's validTo. Returns at most
// one banner — the highest-severity rule that fires — so a member 1 day from
// expiry shows the elevated (error) banner, not the milder warning. An empty or
// non-matching rule set returns null (no config-driven banner).
export function evaluateBannerRules(
    rules: BannerRule[],
    member: { validTo: Date | string },
    now: Dayjs = dayjs(),
): EvaluatedBanner | null {
    const daysUntilExpiry = fromDateOnly(member.validTo).startOf('day').diff(now.startOf('day'), 'day');

    let best: { rule: BannerRule } | null = null;
    for (const rule of rules) {
        if (rule.type !== 'membershipExpiry') continue;
        if (daysUntilExpiry < 0 || daysUntilExpiry > rule.thresholdDays) continue;
        if (best === null || SEVERITY_RANK[rule.severity] > SEVERITY_RANK[best.rule.severity]) {
            best = { rule };
        }
    }

    if (best === null) return null;
    return {
        severity: best.rule.severity,
        message: interpolate(best.rule.message, { daysUntilExpiry }),
    };
}
